import React, { useCallback } from 'react';
import cn from 'classnames';
import Popover, { IPopoverProps } from '@creditflowresearch/uilib/Popover';
import Button from '@creditflowresearch/uilib/Button';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import { usePortfoliosContext } from '@/creditflowresearch/application/PortfolioMonitor/PortfoliosContext';
import {
  IFieldData, TSetFieldsData,
} from '@/creditflowresearch/ui/components/portfolioMonitor/PortfolioContent/ManagePortfoliosModal/ManagePortfoliosModal';
import { useManagePortfoliosContext } from '@/creditflowresearch/application/PortfolioMonitor/ManagePortfoliosContext';

import styles from './deletePortfolioModal.module.scss';
import namePortfolioFormStyles from '@/creditflowresearch/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/FormSteps/NamePortfolioForm/namePortfolioForm.module.scss';

const offsetOptions: IPopoverProps['offsetOptions'] = {
  crossAxis: -10,
  mainAxis: -10,
};

interface IProps {
  fieldData: IFieldData,
  visibleDeleteModalId: number,
  deleteModalId: number,
  onCancel: () => void,
  setFieldsData: TSetFieldsData,
  children: React.ReactElement,
}

const DeletePortfolioModal = ({
  fieldData,
  visibleDeleteModalId,
  deleteModalId,
  onCancel,
  setFieldsData,
  children,
}: IProps) => {
  const { setPortfolioCollection } = usePortfoliosContext();
  const { deletePortfolio, isDeletePortfolioFetching, getPortfolios } = useManagePortfoliosContext();
  const deleteButtonStyles = cn(styles.deleteBtn, {
    [styles.deleteBtnFetching]: isDeletePortfolioFetching,
  });

  const removeFieldData = () => {
    setFieldsData((prevFieldsData: IFieldData[]) => prevFieldsData
      .filter((currFieldsData) => currFieldsData.id !== fieldData.id));
  };

  const removeNonExistingPortfolio = () => {
    removeFieldData();
  };

  const removeExistingPortfolio = () => {
    setPortfolioCollection((prevPortfolios) => prevPortfolios
      .filter((currPortfolio) => currPortfolio.id !== fieldData.portfolio.id));
    removeFieldData();
  };

  const onDelete = useCallback(async () => {
    const isPortfolioExist = !!fieldData.portfolio.id;
    if (isPortfolioExist) {
      const result = await deletePortfolio(fieldData.portfolio);
      if (result) {
        removeExistingPortfolio();
      }
    }

    removeNonExistingPortfolio();
    await getPortfolios();
    onCancel();
    }, [fieldData]);

  const preventClosingPortfolio = (e) => {
    e.stopPropagation();
  };

  const content = (
    <div className={ styles.wrapper } onClick={ preventClosingPortfolio }>
      <div className={ styles.content }>
        <div className={ styles.title }>Delete Portfolio</div>
        <div className={ styles.description }>
          Are you sure you want to delete this portfolio? This action is irreversible.
        </div>
        <div className={ styles.buttons }>
          <Button
            className={ deleteButtonStyles }
            onClick={ onDelete }
            variant="solid"
            color="primary"
            dataTest="deleteButton"
            disabled={ isDeletePortfolioFetching }
          >
            { isDeletePortfolioFetching ? <Spinner className={ namePortfolioFormStyles.spinner } /> : 'Delete Portfolio' }
          </Button>
          <Button
            onClick={ onCancel }
            variant="text"
            color="primary"
            dataTest="cancelButton"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      contentClassName={ styles.popoverContent }
      arrowClassName={ styles.popoverArrow }
      renderOnlyVisible
      isVisible={ visibleDeleteModalId === deleteModalId }
      dataTest="deletePortfolioModal"
      content={ content }
      placement="bottom-end"
      offsetOptions={ offsetOptions }
    >
      { children }
    </Popover>
  );
};

export default DeletePortfolioModal;
